import { gql, useMutation, useQuery } from '@apollo/client';
import type { BankAccount } from '@interfaces/bankAccount';
import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import { SubAccountStates } from '@interfaces/subAccountEnums';
import { Close } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box } from '@ovComponents/1-primative/box/box';
import { Grid } from '@ovComponents/1-primative/grid/grid';
import { Link } from '@ovComponents/1-primative/link/link';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { DateField } from '@ovComponents/2-component/dateField/dateField';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Form } from '@ovComponents/2-component/form/form';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { AmountField } from '@ovComponents/3-pattern/amountField/amountField';
import { BankAccountSelectField } from '@ovComponents/3-pattern/bankAccountSelectField/bankAccountSelect';
import { NumberField } from '@ovComponents/3-pattern/numberField/numberField';
import { SubAccountSelect } from '@ovComponents/3-pattern/subaccountSelect/subAccountSelect';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { dollars2cents } from '@utils/dollars2cents';
import dayjs from 'dayjs';
import { isUndefined, kebabCase } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  decidePaymentInstructionAgeSelection,
  getPaymentInstructionDateOfBirth,
  PaymentInstructionAgeOption,
  type PaymentInstructionAgeSelection,
} from './paymentInstructionAge';

const getAge = (dob?: Date): string => {
  if (!dob) return '0';
  const today = new Date();
  const birthDate = new Date(dob);
  let myAge = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    myAge -= 1;
  }
  return myAge.toString();
};

export const CREATE_SCHEDULED_INCOME_FUND_TRANSFER = gql`
  mutation createScheduledIncomeFundTransfer(
    $input: CreateScheduledIncomeFundTransferInput!
  ) {
    createScheduledIncomeFundTransfer(input: $input) {
      scheduledIncomeFundTransfer {
        id
      }
    }
  }
`;

export const UPDATE_SCHEDULED_INCOME_FUND_TRANSFER = gql`
  mutation updateScheduledIncomeFundTransfer(
    $input: UpdateScheduledIncomeFundTransferInput!
  ) {
    updateScheduledIncomeFundTransfer(input: $input) {
      scheduledIncomeFundTransfer {
        id
      }
    }
  }
`;

export const FETCH_USER = gql`
  query fetchUser($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        dateOfBirth
        language
        bankAccounts {
          id
          name
          bankAccountNumber
        }
      }
    }
  }
`;

enum TaxOptionTypes {
  NO_TAX_ON_MINIMUM = 'NO_TAX_ON_MINIMUM',
  SPECIAL_TAX_RATE = 'SPECIAL_TAX_RATE',
  TAX_ON_FULL = 'TAX_ON_FULL',
}

type PaymentData = {
  totalAnnualWithdrawal: string;
  frequency: string;
  amount: number | undefined;
  amountPayableType: string;
  scheduledDate: string;
  age: PaymentInstructionAgeSelection;
  specialTaxRate: number | undefined;
  taxOption: TaxOptionTypes;
  subAccountId: string;
};

const PAYMENT_INITIAL_DATA: PaymentData = {
  totalAnnualWithdrawal: '',
  frequency: '',
  amount: undefined,
  amountPayableType: '',
  scheduledDate: '',
  age: '',
  taxOption: TaxOptionTypes.NO_TAX_ON_MINIMUM,
  specialTaxRate: 0,
  subAccountId: '',
};

export const PaymentInstructionModal = ({
  open,
  setOpen,
  action = 'create',
  userId,
  refetch,
  account,
  fundTitle,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  action?: 'create' | 'edit';
  userId: string;
  refetch?: any;
  account: any;
  fundTitle?: string;
}) => {
  const { t } = useTranslation(['paymentInstruction']);
  const { sys } = useThemeTokens();
  const { showToast } = useGlobalToast();

  const { data, loading } = useQuery(FETCH_USER, {
    variables: { userId },
    skip: !userId,
    fetchPolicy: 'no-cache',
  });

  const [focused, setFocused] = useState<string[]>([]);
  const [bankAccount, setBankAccount] = useState<BankAccount | null>({
    id: '',
    name: '',
    bankAccountNumber: '',
    bankAccountNumberDisclosed: '',
    institutionNumber: '',
    transitNumber: '',
  });
  const [paymentData, setPaymentData] =
    useState<PaymentData>(PAYMENT_INITIAL_DATA);

  const getInitialWithdrawalOption = (): string => {
    const savedOption =
      account?.scheduledIncomeFundTransfer?.totalWithdrawalAmountOption;
    if (savedOption) return savedOption;

    const annualAmount =
      account?.scheduledIncomeFundTransfer?.annualAmountCents;
    return annualAmount ? 'CUSTOM' : 'MINIMUM';
  };

  useEffect(() => {
    if (!open) return;

    setFocused([]);
    setPaymentData({
      totalAnnualWithdrawal: getInitialWithdrawalOption(),
      frequency: account?.scheduledIncomeFundTransfer?.frequency ?? '',
      amount:
        account?.scheduledIncomeFundTransfer?.annualAmountCents ?? undefined,
      amountPayableType:
        account?.scheduledIncomeFundTransfer?.amountPayableType,
      scheduledDate: account?.scheduledIncomeFundTransfer?.scheduledDate ?? '',
      age: decidePaymentInstructionAgeSelection(
        account?.scheduledIncomeFundTransfer?.dateOfBirth,
        account?.affiliations?.[0]?.user?.dateOfBirth,
      ),
      taxOption:
        account?.scheduledIncomeFundTransfer?.taxOption ??
        TaxOptionTypes.NO_TAX_ON_MINIMUM,
      specialTaxRate:
        Number(account?.scheduledIncomeFundTransfer?.specialTaxRate) ?? 0,
      subAccountId:
        account?.scheduledIncomeFundTransfer?.subAccountId ??
        (filteredSubAccounts.length === 1 ? filteredSubAccounts[0].id : ''),
    });
    setBankAccount({
      id: account?.scheduledIncomeFundTransfer?.bankAccount?.id ?? '',
      name: account?.scheduledIncomeFundTransfer?.bankAccount?.name ?? '',
      bankAccountNumber:
        account?.scheduledIncomeFundTransfer?.bankAccount?.bankAccountNumber ??
        '',
      bankAccountNumberDisclosed: '',
      institutionNumber: '',
      transitNumber: '',
    });
  }, [account, open]);

  const getDOB = () => {
    if (loading && paymentData.age === PaymentInstructionAgeOption.MY_AGE) {
      return '';
    }

    return getPaymentInstructionDateOfBirth(
      paymentData.age,
      data?.fetchUser?.user?.dateOfBirth,
      account.affiliations[0]?.user?.dateOfBirth,
    );
  };

  const filteredSubAccounts = account.subAccounts.filter(
    (subAccount: SubAccount) =>
      [SubAccountStates.ACTIVE, SubAccountStates.AWAITING_APPROVAL].includes(
        subAccount.state,
      ),
  );

  const [updateScheduledIncomeFundTransfer] = useMutation(
    UPDATE_SCHEDULED_INCOME_FUND_TRANSFER,
    {
      variables: {
        input: {
          scheduledIncomeFundTransferId:
            account?.scheduledIncomeFundTransfer?.id,
          dateOfBirth: getDOB(),
          subAccountId:
            paymentData.subAccountId ||
            (account.subAccounts && filteredSubAccounts.length > 0
              ? filteredSubAccounts[0].id
              : ''),
          bankAccountId: bankAccount?.id,
          frequency: paymentData.frequency,
          scheduledDate: paymentData.scheduledDate,
          annualAmountCents:
            paymentData.totalAnnualWithdrawal === 'CUSTOM'
              ? Number(paymentData.amount)
              : 0,
          amountPayableType:
            paymentData.totalAnnualWithdrawal === 'CUSTOM'
              ? paymentData.amountPayableType
              : paymentData.amountPayableType || 'GROSS',
          taxOption: paymentData.taxOption || TaxOptionTypes.NO_TAX_ON_MINIMUM,
          specialTaxRate:
            paymentData.taxOption === TaxOptionTypes.SPECIAL_TAX_RATE
              ? Number(paymentData.specialTaxRate)
              : 0,
          transferredAmountCents: 0,
          totalWithdrawalAmountOption:
            paymentData.totalAnnualWithdrawal || undefined,
        },
      },
      onCompleted: () => {
        refetch && refetch();
        setOpen(false);
      },
    },
  );

  const [createScheduledIncomeFundTransfer] = useMutation(
    CREATE_SCHEDULED_INCOME_FUND_TRANSFER,
    {
      variables: {
        input: {
          dateOfBirth: getDOB(),
          subAccountId:
            paymentData.subAccountId ||
            (account.subAccounts && filteredSubAccounts.length > 0
              ? filteredSubAccounts[0].id
              : ''),
          bankAccountId: bankAccount?.id,
          frequency: paymentData.frequency,
          scheduledDate: paymentData.scheduledDate,
          annualAmountCents:
            paymentData.totalAnnualWithdrawal === 'CUSTOM'
              ? Number(paymentData.amount)
              : 0,
          amountPayableType:
            paymentData.totalAnnualWithdrawal === 'CUSTOM'
              ? paymentData.amountPayableType
              : 'GROSS',
          taxOption: paymentData.taxOption || TaxOptionTypes.NO_TAX_ON_MINIMUM,
          transferredAmountCents: 0,
          specialTaxRate:
            paymentData.taxOption === TaxOptionTypes.SPECIAL_TAX_RATE
              ? Number(paymentData.specialTaxRate)
              : 0,
          totalWithdrawalAmountOption:
            paymentData.totalAnnualWithdrawal || undefined,
        },
      },
      onCompleted: () => {
        refetch && refetch();
        setOpen(false);
      },
    },
  );

  const submit = () => {
    if (!validate()) return;

    if (filteredSubAccounts.length === 0) {
      showToast({
        severity: 'error',
        message: t('paymentInstruction:invalidSubAccountToastMessage'),
      });
      return;
    }

    if (action === 'create') {
      createScheduledIncomeFundTransfer();
      return;
    }
    updateScheduledIncomeFundTransfer();
  };

  const validate = () => {
    const invalidFields: string[] = [];

    if (!paymentData.age) invalidFields.push('age');
    if (!paymentData.frequency) invalidFields.push('paymentFrequency');
    if (!paymentData.scheduledDate) invalidFields.push('scheduledDate');
    if (!bankAccount?.id) invalidFields.push('bankAccount');
    if (!paymentData.totalAnnualWithdrawal)
      invalidFields.push('totalAnnualWithdrawal');
    if (filteredSubAccounts.length > 1 && !paymentData.subAccountId)
      invalidFields.push('subAccount');

    if (paymentData.totalAnnualWithdrawal === 'CUSTOM') {
      if (!paymentData.amount) invalidFields.push('amount');
      if (!paymentData.amountPayableType)
        invalidFields.push('amountPayableType');
    }

    setFocused(invalidFields);
    return invalidFields.length === 0;
  };

  const customLabel = (label: string, hasMarginTop: boolean) => (
    <Typography
      display='flex'
      alignItems='center'
      colorVariant='variant'
      sx={{ mt: hasMarginTop ? 2 : 0 }}
    >
      {label}
      <InfoOutlinedIcon
        style={{ fontSize: '14px', marginLeft: sys.spacing.sm }}
      />
    </Typography>
  );

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ border: 'none', marginBottom: '0px' }}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='headingLarge'>
            {t('paymentInstruction:modal.title')}
          </Typography>
          <IconButton data-testid='icon-btn' onClick={() => setOpen(false)}>
            <Close />
          </IconButton>
        </Box>

        <Typography
          component={'span'}
          variant='bodyMedium'
          sx={{ fontWeight: '400', mt: 2 }}
        >
          <span>
            {t('paymentInstruction:modal.subtitle.1', {
              fundPrefix: fundTitle,
            })}
          </span>
          <Link sx={{ mx: 0.5 }}>
            {t('paymentInstruction:modal.subtitle.2')}
          </Link>
          <span>{t('paymentInstruction:modal.subtitle.3')}</span>
        </Typography>
      </DialogTitle>

      <Form onSubmit={submit}>
        <DialogContent>
          {customLabel(t('paymentInstruction:modal.ageSelectLabel'), false)}
          <SelectField
            fullWidth
            label=''
            testId='select-age'
            value={paymentData.age}
            onChange={(e: any) =>
              setPaymentData({
                ...paymentData,
                age: e.target.value as PaymentInstructionAgeSelection,
              })
            }
            onBlur={() => setFocused([...focused, 'age'])}
            error={!paymentData.age && focused.includes('age')}
          >
            {!loading && data?.fetchUser?.user.dateOfBirth && (
              <MenuItem
                value={PaymentInstructionAgeOption.MY_AGE}
                data-testid='select-age-my-age'
              >
                {t('paymentInstruction:modal.myAgeSelectOption', {
                  age: getAge(data.fetchUser.user.dateOfBirth),
                })}
              </MenuItem>
            )}
            {!loading && account.affiliations.length > 0 && (
              <MenuItem
                data-testid='select-age-spouse-age'
                value={PaymentInstructionAgeOption.SPOUSE_AGE}
              >
                {t('paymentInstruction:modal.mySpousesAgeSelectOption', {
                  age: getAge(account.affiliations[0].user.dateOfBirth),
                })}
              </MenuItem>
            )}
          </SelectField>

          {customLabel(
            t('paymentInstruction:modal.totalAnnualWithdrawalLabel'),
            true,
          )}
          <SelectField
            fullWidth
            label=''
            testId='total-withdrawal-amount'
            value={paymentData.totalAnnualWithdrawal}
            onChange={(e: any) =>
              setPaymentData({
                ...paymentData,
                totalAnnualWithdrawal: e.target.value,
              })
            }
            onBlur={() => setFocused([...focused, 'totalAnnualWithdrawal'])}
            error={
              !paymentData.totalAnnualWithdrawal &&
              focused.includes('totalAnnualWithdrawal')
            }
          >
            <MenuItem
              data-testid='total-withdrawal-amount-option-minimum'
              value='MINIMUM'
            >
              {t(
                'paymentInstruction:modal.totalAnnualWithdrawalMinimumAmountOption',
              )}
            </MenuItem>
            <MenuItem
              data-testid='total-withdrawal-amount-option-maximum'
              value='MAXIMUM'
            >
              {t(
                'paymentInstruction:modal.totalAnnualWithdrawalMaximumAmountOption',
              )}
            </MenuItem>
            <MenuItem
              data-testid='total-withdrawal-amount-option-custom'
              value='CUSTOM'
            >
              {t(
                'paymentInstruction:modal.totalAnnualWithdrawalCustomAmountOption',
              )}
            </MenuItem>
          </SelectField>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {paymentData.totalAnnualWithdrawal === 'CUSTOM' && (
              <Grid item xs={6}>
                <AmountField
                  testId='total-annual-withdrawal'
                  label={t('paymentInstruction:modal.amountLabel')}
                  amount={
                    paymentData.amount
                      ? (paymentData.amount / 100).toString()
                      : ''
                  }
                  setAmount={(e) =>
                    setPaymentData({
                      ...paymentData,
                      amount: dollars2cents(e),
                    })
                  }
                  onBlur={() => setFocused([...focused, 'amount'])}
                  error={!paymentData.amount && focused.includes('amount')}
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <SelectField
                fullWidth
                testId='payment-frequency'
                label={t('paymentInstruction:modal.paymentFrequencyLabel')}
                value={paymentData.frequency}
                onChange={(e: any) =>
                  setPaymentData({ ...paymentData, frequency: e.target.value })
                }
                onBlur={() => setFocused([...focused, 'paymentFrequency'])}
                error={
                  !paymentData.frequency && focused.includes('paymentFrequency')
                }
              >
                {['ANNUALLY', 'SEMI_ANNUALLY', 'QUARTERLY', 'MONTHLY'].map(
                  (value) => (
                    <MenuItem
                      key={value}
                      data-testid={`payment-frequency-option-${kebabCase(value)}`}
                      value={value}
                    >
                      {t(`paymentInstruction:modal.${value}`)}
                    </MenuItem>
                  ),
                )}
              </SelectField>
            </Grid>
            {paymentData.totalAnnualWithdrawal === 'CUSTOM' && (
              <Grid item xs={6}>
                {customLabel(
                  t('paymentInstruction:modal.withdrawalTypeLabel'),
                  false,
                )}
                <SelectField
                  fullWidth
                  label=''
                  testId='withdrawal-type'
                  value={paymentData.amountPayableType}
                  onChange={(e: any) =>
                    setPaymentData({
                      ...paymentData,
                      amountPayableType: e.target.value,
                    })
                  }
                  onBlur={() => setFocused([...focused, 'amountPayableType'])}
                  error={
                    !paymentData.amountPayableType &&
                    focused.includes('amountPayableType')
                  }
                >
                  <MenuItem
                    data-testid='withdrawal-type-option-net'
                    value='NET'
                  >
                    {t('paymentInstruction:modal.withdrawalTypeNetOption')}
                  </MenuItem>
                  <MenuItem
                    data-testid='withdrawal-type-option-gross'
                    value='GROSS'
                  >
                    {t('paymentInstruction:modal.withdrawalTypeGrossOption')}
                  </MenuItem>
                </SelectField>
              </Grid>
            )}
            <Grid item xs={6}>
              <DateField
                fullWidth
                dataTestId='start-date'
                onChange={(date: any) =>
                  setPaymentData({
                    ...paymentData,
                    scheduledDate: dayjs(date?.toString()).format('YYYY-MM-DD'),
                  })
                }
                disabled={loading}
                label={t('paymentInstruction:modal.startDateLabel')}
                value={paymentData.scheduledDate}
                onBlur={() => setFocused([...focused, 'scheduledDate'])}
                error={
                  !paymentData.scheduledDate &&
                  focused.includes('scheduledDate')
                }
              />
            </Grid>
          </Grid>

          <Grid sx={{ mt: 2 }}>
            <SelectField
              value={paymentData.taxOption}
              label={t('components:scheduledFundTransferModal.taxOptionType')}
              fullWidth
              onChange={(e: any) => {
                setPaymentData({ ...paymentData, taxOption: e.target.value });
              }}
              data-testid='select-tax-option-type'
            >
              <MenuItem key='NO_TAX_ON_MINIMUM' value='NO_TAX_ON_MINIMUM'>
                {t(
                  'components:scheduledFundTransferModal.taxOptions.NO_TAX_ON_MINIMUM',
                )}
              </MenuItem>
              <MenuItem key='SPECIAL_TAX_RATE' value='SPECIAL_TAX_RATE'>
                {t(
                  'components:scheduledFundTransferModal.taxOptions.SPECIAL_TAX_RATE',
                )}
              </MenuItem>
              <MenuItem key='TAX_ON_FULL' value='TAX_ON_FULL'>
                {t(
                  'components:scheduledFundTransferModal.taxOptions.TAX_ON_FULL',
                )}
              </MenuItem>
            </SelectField>
          </Grid>
          {paymentData.taxOption === TaxOptionTypes.SPECIAL_TAX_RATE && (
            <Grid sx={{ mt: 2 }}>
              <NumberField
                label={t(
                  'components:scheduledFundTransferModal.specialTaxRate',
                )}
                decimalPlaces={2}
                number={paymentData.specialTaxRate?.toString() ?? ''}
                setNumber={(value: any) => {
                  setPaymentData({ ...paymentData, specialTaxRate: value });
                }}
                error={
                  !isUndefined(paymentData.specialTaxRate) &&
                  (paymentData.specialTaxRate > 100 ||
                    paymentData.specialTaxRate < 0)
                }
                sx={{ mt: 2 }}
                data-testid='number-special-tax-rate'
              />
            </Grid>
          )}

          {filteredSubAccounts.length > 1 && (
            <SubAccountSelect
              label={t('paymentInstruction:modal.subAccountLabel')}
              onSubAccountSelect={(subAccount) => {
                if (subAccount) {
                  setPaymentData((prev) => ({
                    ...prev,
                    subAccountId: subAccount.id,
                  }));
                }
              }}
              selectedSubAccount={filteredSubAccounts.find(
                (sa: SubAccount) => sa.id === paymentData.subAccountId,
              )}
              filter={{ accountId: account.accountId }}
              sx={{ mt: 2 }}
              dataTestId='sub-account-select'
              omitAny={true}
              error={
                !paymentData.subAccountId && focused.includes('subAccount')
              }
              onBlur={() => setFocused([...focused, 'subAccount'])}
            />
          )}

          <BankAccountSelectField
            data-testid='bank-account-select'
            sx={{ mt: 2 }}
            setBankAccount={setBankAccount}
            bankAccount={bankAccount}
            userId={userId}
            onBlur={() => setFocused([...focused, 'bankAccount'])}
            error={!bankAccount?.id && focused.includes('bankAccount')}
          />
          <Box
            bgcolor={sys.color.surfaceContainer}
            borderRadius={sys.borderRadius.md}
            padding={sys.spacing.xl}
            marginTop={sys.spacing.xxl}
          >
            <Typography
              component='span'
              variant='bodyMedium'
              sx={{ fontWeight: '400' }}
            >
              <p>{t('paymentInstruction:modal.note1')}</p>
              <p>{t('paymentInstruction:modal.note2')}</p>
            </Typography>
          </Box>
        </DialogContent>
        <DialogFooter>
          <Box display='flex' justifyContent='end' p={1}>
            <Button
              dataTestId='payment-instructions-done-btn'
              label={
                action === 'create' ? t('shared:done') : t('client:form.update')
              }
              type='submit'
              variant='tonal'
            />
          </Box>
        </DialogFooter>
      </Form>
    </Dialog>
  );
};
